import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { Add, Delete, Edit, Done, Cancel, Search } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import useConfirmDialog from "services/hooks/useConfirmDialog";

const ROLE_LABELS = { agent: "Agent", manager: "Manager" };

export default function CRMMembersManager() {
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const headers = { Authorization: `Token ${token}` };
  const { ConfirmDialog, showConfirmDialog } = useConfirmDialog();

  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ user: "", role: "agent", manager: "" });

  // Filtering & Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${domain}crm/crm-members/`, { headers });
      setMembers(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch (err) {
      console.error("Failed to load CRM members:", err);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, token]);

  const loadAllUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${domain}crm/product-based/user/?product=CRM`, {
        headers,
      });
      setAllUsers(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, token]);

  useEffect(() => {
    loadMembers();
    loadAllUsers();
  }, [loadMembers, loadAllUsers]);

  // Improvements: Filter only active users for dropdown options
  const activeAllUsers = allUsers.filter((u) => u.is_active !== false);

  const openAddDialog = () => {
    setEditingId(null);
    setForm({ user: "", role: "agent", manager: "" });
    setOpenDialog(true);
  };

  const openEditDialog = (member) => {
    setEditingId(member.id);
    setForm({
      user: member.user,
      role: member.role,
      manager: member.manager || "",
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!form.user && !editingId) {
      toast.error("Select a user.");
      return;
    }
    
    // Maintain existing is_active status on edit to prevent accidental reactivation
    const currentMember = editingId ? members.find((m) => m.id === editingId) : null;
    
    const payload = {
      role: form.role,
      manager: form.manager || null,
      is_active: currentMember ? currentMember.is_active : true,
    };

    // Only send the user mapping on creation. 
    if (!editingId) {
      payload.user = form.user;
    }

    try {
      if (editingId) {
        await axios.put(`${domain}crm/crm-members/${editingId}/`, payload, {
          headers,
        });
      } else {
        await axios.post(`${domain}crm/crm-members/`, payload, { headers });
      }
      setOpenDialog(false);
      loadMembers();
    } catch (err) {
      let message =
        err?.response?.data?.detail ||
        err?.response?.data?.user?.[0] ||
        err?.response?.data?.manager?.[0] ||
        err?.response?.data?.non_field_errors?.[0] ||
        "Failed to save CRM member.";
      
      // Provide a clean duplicate creation error 
      if (typeof message === "string" && message.toLowerCase().includes("already exists")) {
        message = "This user is already a CRM member.";
      }
      toast.error(message);
    }
  };

  const handleDeactivate = (member) => {
    showConfirmDialog({
      title: "Deactivate CRM Member",
      description: `Deactivate ${member.username || "this user"} as a CRM member? Their historical records remain unchanged.`,
      onConfirm: async () => {
        try {
          await axios.delete(`${domain}crm/crm-members/${member.id}/`, {
            headers,
          });
          loadMembers();
        } catch (err) {
          console.error("Deactivate error:", err);
          toast.error("Failed to deactivate member.");
        }
      },
    });
  };

  // Filter Data Source for table UI
  const filteredMembers = members.filter((m) => {
    const matchesRole = filterRole === "all" || m.role === filterRole;
    const matchesSearch = m.username?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesRole && matchesSearch;
  });

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            CRM Members
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage who has access to the CRM, their role, and reporting manager.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openAddDialog}
          sx={{
            backgroundColor: "#84DEB8",
            color: "#000",
            "&:hover": { backgroundColor: "#6cc9a1" },
            whiteSpace: "nowrap"
          }}
        >
          Add Member
        </Button>
      </Box>

      {/* Toolbar - Search Box & Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <TextField
          placeholder="Search..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Roles</MenuItem>
          <MenuItem value="agent">Agent</MenuItem>
          <MenuItem value="manager">Manager</MenuItem>
        </Select>
      </Box>

      {loading ? (
        <Skeleton variant="rounded" height={300} sx={{ width: "100%" }} />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: "bold", py: 2 }}>User</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", py: 2 }}>Role</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", py: 2 }}>Manager</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", py: 2 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", py: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMembers.map((member) => (
                <TableRow key={member.id} hover>
                  <TableCell align="center" sx={{ py: 1.5 }}>{member.username}</TableCell>
                  <TableCell align="center" sx={{ py: 1.5 }}>{ROLE_LABELS[member.role] || member.role}</TableCell>
                  <TableCell align="center" sx={{ py: 1.5 }}>{member.manager_name || "—"}</TableCell>
                  <TableCell align="center" sx={{ py: 1.5 }}>
                    <Chip
                      label={member.is_active ? "Active" : "Inactive"}
                      size="small"
                      color={member.is_active ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1.5 }}>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => openEditDialog(member)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {member.is_active && (
                      <Tooltip title="Deactivate">
                        <IconButton onClick={() => handleDeactivate(member)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredMembers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    No CRM members found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ConfirmDialog />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {editingId ? "Edit CRM Member" : "Add CRM Member"}
          <IconButton
            aria-label="close"
            onClick={() => setOpenDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="User"
            value={form.user}
            onChange={(e) => setForm({ ...form, user: e.target.value })}
            disabled={!!editingId}
            sx={{ mt: 2, mb: 2 }}
          >
            {activeAllUsers.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.username}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            sx={{ mb: 2 }}
          >
            <MenuItem value="agent">Agent</MenuItem>
            <MenuItem value="manager">Manager</MenuItem>
          </TextField>
          <Select
            fullWidth
            displayEmpty
            value={form.manager}
            onChange={(e) => setForm({ ...form, manager: e.target.value })}
          >
            <MenuItem value="">— No manager —</MenuItem>
            {members
              // Allow managers to appear if they're active OR if they are currently assigned to this record
              .filter((m) => (m.role === "manager" && m.is_active) || m.id === form.manager)
              .filter((m) => m.id !== editingId)
              .map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.username}
                </MenuItem>
              ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} startIcon={<Cancel />} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Done />}
            onClick={handleSave}
            sx={{
              backgroundColor: "#84DEB8",
              color: "#000",
              "&:hover": { backgroundColor: "#6cc9a1" },
            }}
          >
            {editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}